import { createReducer, on } from '@ngrx/store';
import { CantieriActions } from './cantieri.actions';
import {
  Cantiere,
  CantiereFlussoMensileStats,
  CantierePieStats,
  CantieriQueryParams,
  ContrattoCantiere
} from '../../core/models/cantieri.model';

// Stato locale feature "cantieri" nello store NgRx.
export interface CantieriState {
  // Lista + stato caricamento lista
  items: Cantiere[];
  loading: boolean;
  error: string | null;
  contratti: ContrattoCantiere[];
  contrattiLoading: boolean;

  pieStats: CantierePieStats | null;
  flussoMensileStats: CantiereFlussoMensileStats[];

  // Metadati paginazione lato server
  totalElements: number;
  totalPages: number;
  pageIndex: number;
  pageSize: number;
  // Ultima query usata per la lista (serve per refresh automatico post-mutation)
  lastQuery: CantieriQueryParams | undefined;

  // Dettaglio attualmente selezionato
  selected: Cantiere | null;
  // Contatore usato da alcuni componenti per reagire ai create success senza ascoltare actions$
  createSuccessCount: number;

  // Stato operazioni singole (create/update/delete/associate/...)
  opLoading: boolean;
  opError: string | null;
}

// Valori iniziali store feature cantieri.
export const initialState: CantieriState = {
  items: [],
  loading: false,
  error: null,

  pieStats: null,
  flussoMensileStats: [],

  contratti: [],
  contrattiLoading: false,

  totalElements: 0,
  totalPages: 0,
  pageIndex: 0,
  pageSize: 20,
  lastQuery: undefined,

  selected: null,
  createSuccessCount: 0,

  opLoading: false,
  opError: null,
};

// Utility confronto id robusto (number/string/null-safe).
const toId = (v: unknown) => String(v ?? '');

export const cantieriReducer = createReducer(
  initialState,

  // LISTA
  on(CantieriActions.loadList, (s, { params }) => ({ ...s, loading: true, error: null, lastQuery: params })),
  on(CantieriActions.loadListSuccess, (s, { items, totalElements, totalPages, pageIndex }) => ({
    ...s,
    loading: false,
    items,
    totalElements,
    totalPages,
    pageIndex,
  })),
  on(CantieriActions.loadListFailure, (s, { error }) => ({ ...s, loading: false, error })),

  // DETTAGLIO
  on(CantieriActions.loadDetails, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.loadDetailsSuccess, (s, { item }) => ({ ...s, opLoading: false, selected: item })),
  on(CantieriActions.loadDetailsFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // CREATE
  on(CantieriActions.create, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.createSuccess, (s) => ({ ...s, opLoading: false, createSuccessCount: s.createSuccessCount + 1 })),
  on(CantieriActions.createFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // UPDATE
  on(CantieriActions.update, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.updateSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.updateFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // DELETE
  on(CantieriActions.delete, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.deleteSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    items: s.items.filter((x) => toId(x.id) !== toId(id)),
    selected: toId(s.selected?.id) === toId(id) ? null : s.selected,
  })),
  on(CantieriActions.deleteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // ARCHIVIAZIONE
  on(CantieriActions.archive, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.archiveSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.archiveFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(CantieriActions.unarchive, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.unarchiveSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.unarchiveFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // CONCLUSIONE CANTIERE
  on(CantieriActions.end, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.endSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.endFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // ASSOCIA RESPONSABILE
  on(CantieriActions.associateDipendente, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.associateDipendenteSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.associateDipendenteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // ASSOCIA FORNITORE
  on(CantieriActions.associateFornitore, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.associateFornitoreSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.associateFornitoreFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  // CREA DA PREVENTIVO
  on(CantieriActions.createFromPreventivo, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CantieriActions.createFromPreventivoSuccess, (s) => ({ ...s, opLoading: false })),
  on(CantieriActions.createFromPreventivoFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(CantieriActions.loadContratti, state => ({
    ...state,
    contrattiLoading: true,
  })),

  on(CantieriActions.loadContrattiSuccess, (state, { items }) => ({
    ...state,
    contratti: items,
    contrattiLoading: false,
  })),

  on(CantieriActions.loadContrattiFailure, state => ({
    ...state,
    contrattiLoading: false,
  })),

  on(CantieriActions.loadPieStatsSuccess, (state, { data }) => ({
    ...state,
    pieStats: data,
  })),

  on(CantieriActions.loadFlussoMensileStatsSuccess, (state, { data }) => ({
    ...state,
    flussoMensileStats: data,
  })),
  );
